import React, { useEffect, useState } from 'react';
import { Thermometer, AlertTriangle, Battery } from 'lucide-react';
import SensorTable from '../components/sensors/SensorTable';
import { useGetZonesQuery, useGetZoneSensorsQuery } from '../services/zoneApi';
import { useGetSensorListQuery } from '../services/sensorApi';
import { useGetLiveDataQuery } from '../services/sensorDataApi';
import { formatSensorSerial } from '../utils/sensorUtils';
import { isLowBattery } from '../utils/batteryUtils';
import type { Sensor } from '../types';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useGetUsersQuery } from '../services/userApi';
import { useGetAlarmLogQuery, useGetAlarmsQuery } from '../services/alarmApi';

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
    return (error as any).message;
  }
  return 'Unknown error';
}

export default function DashboardPage() {
  const [selectedZone, setSelectedZone] = useState<number | null>(() => {
    const savedZone = localStorage.getItem('selectedZone');
    return savedZone ? Number(savedZone) : null;
  });
  
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: users } = useGetUsersQuery();
  const { data: zones } = useGetZonesQuery();
  const { data: sensors, isLoading: sensorsLoading, error: sensorsError } = useGetSensorListQuery();
  const { data: zoneSensors } = useGetZoneSensorsQuery(selectedZone || 0, {
    skip: !selectedZone
  });
  const { data: liveData } = useGetLiveDataQuery();
  const { data: alarmLog } = useGetAlarmLogQuery(undefined, {
    skip: !user,
    pollingInterval: 10000, // 10 seconds
  });
  const { data: alarms } = useGetAlarmsQuery(undefined, { skip: !user });
  const alarmSettingsMap = React.useMemo(() => {
    if (!alarms) return {};
    // Support both possible alarm object shapes
    return alarms.reduce((acc: any, alarm: any) => {
      acc[alarm.sensor_id ?? alarm.sensorId] = alarm;
      return acc;
    }, {});
  }, [alarms]);

  // Get the current user's ID from the users list
  const currentUserId = React.useMemo(() => {
    if (!users || !user) return null;
    const currentUser = users.find(u => u.firstName === user.username);
    console.log('Looking for user with firstName:', user.username);
    console.log('Found user:', currentUser);
    return currentUser?.id;
  }, [users, user]);

  // Save selected zone to localStorage whenever it changes
  useEffect(() => {
    if (selectedZone === null) {
      localStorage.removeItem('selectedZone');
    } else {
      localStorage.setItem('selectedZone', selectedZone.toString());
    }
  }, [selectedZone]);

  // Update loading condition to be more specific
  const loading = sensorsLoading && !sensors;
  const error = sensorsError ? 'Failed to load sensors' : null;

  const filteredSensors = React.useMemo(() => {
    if (!selectedZone) return sensors;
    if (!sensors) return [];
    if (!zoneSensors) return [];

    const sensorsInZone = zoneSensors.reduce<number[]>((acc, item) => {
      if(item.zone_ids){
        const ids = String(item.zone_ids).split(',').map(Number);
        if(ids.includes(selectedZone)){
          acc.push(item.sensor_id);
        }
      }
      return acc;
    }, []);

    return sensors.filter(sensor => sensorsInZone.includes(sensor.id));
  }, [sensors, selectedZone, zoneSensors]);
  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading sensors...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 p-4 rounded-md">
          <div className="text-red-700">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Monitor your sensors in real-time
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-2 sm:space-y-0">
          <div className="flex flex-col space-y-2">
            <select
              value={selectedZone || ''}
              onChange={(e) => {
                const value = e.target.value ? Number(e.target.value) : null;
                setSelectedZone(value);
              }}
              className="block w-full sm:w-48 pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">All Zones</option>
              {zones?.map((zone) => (
                <option key={zone.zone_id} value={zone.zone_id}>
                  {zone.zone_name} ({zone.sensor_count})
                </option>
              ))}
            </select>
            
            {selectedZone && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {/* TODO: Implement PDF export */}}
                  className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  title="Export to PDF"
                >
                  <span>PDF</span>
                  <span className="ml-2">📄</span>
                </button>

                <button
                  onClick={() => {/* TODO: Implement Excel export */}}
                  className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  title="Export to Excel"
                >
                  <span>XLS</span>
                  <span className="ml-2">📊</span>
                </button>

                <button
                  onClick={() => {/* TODO: Implement Image export */}}
                  className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  title="Export as Image"
                >
                  <span>IMG</span>
                  <span className="ml-2">🖼️</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg sm:mx-0 -mx-4">
        <div className="px-4 py-5 sm:p-6">
          <SensorTable
            sensors={filteredSensors ?? []}
            alarmLog={alarmLog ?? []}
            alarmSettingsMap={alarmSettingsMap}
            showFirmwareUpdate={false}
            enableDragAndDrop={true}
          />
        </div>
      </div>
    </div>
  );
}